package com.devi3east.bashbuddy.leaderboard.ui

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.auth.ui.AuthUiState
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.leaderboard.domain.models.OrganizationLeaderboardItem
import com.devi3east.bashbuddy.leaderboard.ui.components.EmptyLeaderboardContent
import com.devi3east.bashbuddy.leaderboard.ui.components.LeaderboardRules
import com.devi3east.bashbuddy.leaderboard.ui.components.OrganizationLeaderboardCard

@Composable
fun LeaderboardScreenRoute(
    appState: AppState
) {
    val uiState by appState.authViewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        appState.authViewModel.loadLeaderboard()
    }

    LeaderboardScreen(
        uiState = uiState,
        leaderboard = uiState.leaderboard,
        onBack = { appState.navController.popBackStack() }
    )
}

@Composable
fun LeaderboardScreen(
    uiState: AuthUiState,
    leaderboard: List<OrganizationLeaderboardItem>,
    onBack: () -> Unit
) {
    var selectedTab by remember { mutableStateOf("Leaderboard") }
    val toggleOptions = listOf("Leaderboard", "Rules")

    val eligibleLeaderboard = leaderboard
        .filter {
            it.eventCount > 0 &&
                    it.stripeOnboardingComplete
        }
        .sortedByDescending { it.affiliateCount }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
    ) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Box(
            modifier = Modifier.fillMaxSize()
        ) {
            when {
                uiState.isLoading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }

                uiState.errorMessage != null -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = uiState.errorMessage,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
                else -> {
                    Column(
                        modifier = contentModifier.padding(horizontal = 16.dp)
                    ) {

                        // Header
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 8.dp)
                        ) {
                            IconButton(
                                onClick = onBack,
                                modifier = Modifier.align(Alignment.CenterStart)
                            ) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                    contentDescription = "Back",
                                    tint = MaterialTheme.colorScheme.onSurface
                                )
                            }

                            Row(
                                modifier = Modifier.align(Alignment.Center),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                Text(
                                    text = "Organization Leaderboard",
                                    style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }
                        }

                        Spacer(Modifier.height(16.dp))

                        // Toggle row
                        Row(
                            modifier = Modifier
                                .widthIn(max = targetWidth)
                                .align(Alignment.CenterHorizontally)
                                .padding(horizontal = 16.dp, vertical = 6.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            toggleOptions.forEach { label ->
                                val isSelected = selectedTab == label

                                val backgroundColor by animateColorAsState(
                                    if (isSelected)
                                        MaterialTheme.colorScheme.primaryContainer
                                    else
                                        MaterialTheme.colorScheme.surface.copy(alpha = 0.6f)
                                )

                                val textColor by animateColorAsState(
                                    if (isSelected)
                                        MaterialTheme.colorScheme.onPrimaryContainer
                                    else
                                        MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                                )

                                val shape = RoundedCornerShape(12.dp)

                                Surface(
                                    color = backgroundColor,
                                    shape = shape,
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                                    modifier = Modifier
                                        .weight(1f)
                                        .clip(shape)
                                        .clickable { selectedTab = label }
                                        .heightIn(48.dp)
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(vertical = 8.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = label,
                                            color = textColor,
                                            style = MaterialTheme.typography.bodyLarge,
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }
                            }
                        }

                        Spacer(Modifier.height(8.dp))

                        // ✅ Content area — no nested scroll containers
                        when (selectedTab) {

                            "Leaderboard" -> {
                                LazyColumn(
                                    modifier = Modifier
                                        .weight(1f)
                                        .fillMaxWidth()
                                        .padding(horizontal = 16.dp, vertical = 12.dp),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    if (eligibleLeaderboard.isEmpty()) {
                                        item { EmptyLeaderboardContent() }
                                    } else {
                                        items(eligibleLeaderboard) { org ->
                                            OrganizationLeaderboardCard(item = org)
                                        }
                                    }
                                }
                            }

                            "Rules" -> {
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .fillMaxWidth()
                                        .padding(horizontal = 16.dp, vertical = 12.dp)
                                ) {
                                    LeaderboardRules(
                                        date = "May 1, 2026"
                                    )
                                }
                            }
                        }
                    }
                }

            }
        }
    }
}